import { ReplayViewMode } from '@common/Replay/types';
import { useIfOnboardingReplay, useReplayModeType } from '@common/Replay/utils';
import { Dialog, formatDate } from '@prophecy/ui-v3';
import { DialogResponseAction } from '@prophecy/ui-v3/Dialog/types';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { replayToken } from 'frontend/core/src/utils/replayToken';
import { shareToken } from 'frontend/core/src/utils/shareToken';
import isEmail from 'validator/lib/isEmail';

export const delimiters = /[,;\s]/; // delimiters (comma, semicolon, space)

export const parseEmails = (text: string): string[] => {
  return text
    .split(delimiters)
    .map((email) => email.trim())
    .filter((email) => email && isEmail(email));
};

export const formatReplayDate = (date: number | undefined) => {
  if (!date) return '';
  return `on ${formatDate(new Date(date), 'd MMM, yyyy hh:mm')}`;
};

export const getShareIdFromLink = (shareLink: string) => {
  if (!shareLink) return '';
  const url = new URL(shareLink);
  const shareId = url.pathname.split('/').at(-1);
  return shareId ?? '';
};

export enum GuestUserClickResultEnum {
  login = 'login',
  leave = 'leave',
  cancel = 'cancel'
}

export const useGuestUserClick = () => {
  const { replayMode, replayId } = useReplayModeType();
  const isOnboardingReplay = useIfOnboardingReplay({ replayId });

  return usePersistentCallback(async () => {
    const isGuestLoggedIn = Boolean(shareToken.loggedInUserId || replayToken.loggedInUserID);
    const replayModeShare = Boolean(replayToken.get() && replayMode !== ReplayViewMode.edit);
    const isSharedProject = Boolean(shareToken.get());

    let dialogSubTitle: string;

    if (isGuestLoggedIn) {
      const projectType = replayMode ? 'replay' : 'shared';
      const linkType = replayMode ? 'replay URL' : 'shared URL';

      if (isSharedProject || replayModeShare) {
        dialogSubTitle = isOnboardingReplay
          ? `Are you sure you want to leave? Current replay progress will be saved and can be resumed from home page again.`
          : `Are you sure you want to leave? You can return to this ${projectType} project anytime using the ${linkType}.`;
      } else {
        dialogSubTitle = `Leaving now will reset your ${projectType} project progress. You can restart the ${projectType} anytime using the ${linkType}.`;
      }
    } else {
      const projectType = isSharedProject ? 'shared' : 'replay';
      dialogSubTitle = `You are currently viewing a ${projectType} project as a guest. Please log in to continue.`;
    }

    const allowedDialogResult = await Dialog.warning({
      title: `Leaving?`,
      subTitle: dialogSubTitle,
      size: 'l',
      closable: true,
      showCloseIcon: true,
      closeButton: { children: 'Cancel' },
      okButton: { children: !isGuestLoggedIn ? 'Login' : 'Leave', block: false },
      'data-test-id': 'replay-exit-dialog'
    });

    if (allowedDialogResult === DialogResponseAction.okButton) {
      if (isGuestLoggedIn) {
        return GuestUserClickResultEnum.leave;
      } else {
        return GuestUserClickResultEnum.login;
      }
    }

    return GuestUserClickResultEnum.cancel;
  });
};
